/**
 * `shell/DemoExitButton.tsx` — the way out of a `/demo` session (#2779).
 *
 * A demo visitor has no account to sign out of: what they want is the
 * website again. The button calls `leaveDemo()`, which clears the stored
 * demo token (the server sweeps the team at its 2 h TTL) and hard-loads
 * `/`, where the signed-out gate paints the landing page.
 * Renders nothing outside a demo session.
 *
 * Two placements: `<DemoExitButton/>` sits in the top bar from `xl` up.
 * Below `xl` the bar has no room (it covered the logo at 390 px and
 * squeezed the team switcher to one letter at 900 px), so `<DemoExitRow/>`
 * gives the same button its own row under the bar.
 */
import { ArrowLeft } from "lucide-react";

import { useAuth } from "../../lib/AuthContext";
import { cn } from "../../lib/cn";
import { leaveDemo } from "../../lib/demoSession";
import { Button } from "../ui";

function ExitButton({ className, testid }: { className?: string; testid: string }) {
  return (
    <Button
      size="sm"
      className={cn("gap-1.5", className)}
      data-testid={testid}
      onClick={() => leaveDemo()}
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Back to the website
    </Button>
  );
}

/** The top-bar placement, `xl` and up. */
export function DemoExitButton() {
  const { mode } = useAuth();
  if (mode !== "demo") return null;
  return <ExitButton className="hidden xl:inline-flex" testid="demo-exit-button" />;
}

/** Below `xl`: its own row under the top bar. */
export function DemoExitRow() {
  const { mode } = useAuth();
  if (mode !== "demo") return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 pb-3 sm:px-6 lg:px-8 xl:hidden">
      <span className="text-xs text-fg-muted">You are in the demo.</span>
      <ExitButton className="h-auto min-h-tap" testid="demo-exit-row-button" />
    </div>
  );
}
